<script>
import { Row, Col, Cell, Calendar, DatetimePicker, Popup } from 'vant'
import Common from '@/tbs/model/to/Common'
import DateTimeFunc from '@/tbs/util/DateTimeFunc'

const common = new Common

export default {
    //--------------------------------------------------
	// component name
	//--------------------------------------------------
    name: 'date-time-selector',

    //--------------------------------------------------
	// component components
	//--------------------------------------------------
    components: {
        'van-row': Row,
        'van-col': Col,
        'van-cell': Cell,
        'van-calendar': Calendar,
        'van-datetime-picker': DatetimePicker,
        'van-popup': Popup,
    },

    //--------------------------------------------------
    // component props
    //--------------------------------------------------
    props: {
        label:String,
        datePlaceholder:String,
        timePlaceholder:String,
        calendarValue:Date,
        timeValue:String,
        dateFormat:String,
        disabled: Boolean,
        clearable: Boolean,
	},

    //--------------------------------------------------
    // component emits
    //--------------------------------------------------
    emits: ['change-data'],

    //--------------------------------------------------
    // component data
    //--------------------------------------------------
    data() {
        return {
            showCalendar: false,
            selectedCalendarValue: null,
            showTime: false,
            selectedTimeValue: null,
        }
    },

    //--------------------------------------------------
    // computed setter and getter
    //--------------------------------------------------
    computed: {
        dateLabel() {
            let selectedLabel = this.datePlaceholder
            const format = this.dateFormat || 'yyyy-MM-dd'
            if (this.selectedCalendarValue) {
                // selectedLabel = DateTimeFunc.formatDate(this.selectedCalendarValue, format)
                selectedLabel = common.localeProp('weekday-date-time', {
                    date: DateTimeFunc.formatDate(this.selectedCalendarValue, format),
                    day: '${weekday-' + this.selectedCalendarValue.getDay() + '}',
                    time: ''
                })
            }
            return selectedLabel
        },
        timeLabel() {
            let selectedLabel = this.timePlaceholder
            if (this.selectedTimeValue) {
                selectedLabel = this.selectedTimeValue
            }
            return selectedLabel
        },
        minDate() {
            const today = new Date
            const oneYear = 12 * 30 * 24 * 60 * 60 * 1000
            return new Date(today.getTime() - oneYear)
        },
        defaultDate() {
            return this.calendarValue ? this.calendarValue : (new Date)
        }
    },

    watch: {
        calendarValue(value) {
            console.log('@watch calendarValue')
            this.selectedCalendarValue = value
        },
        timeValue(value) {
            console.log('@watch timeValue')
            this.selectedTimeValue = value
        }
    },

    mounted() {
        // console.log('mounted')
        this.selectedCalendarValue = this.calendarValue
        this.selectedTimeValue = this.timeValue
    },

    //--------------------------------------------------
    // component methods
    //--------------------------------------------------
    methods: {
        onCalendarConfirm(data) {
            console.log('onCalendarConfirm', data)
            this.selectedCalendarValue = data
            this.showCalendar = false
            this.changeData()
        },
        onCalendarLinkClick() {
            if (!this.disabled) {
                this.showCalendar = true
            }
        },
        timeFilter(type, options) {
            if (type === 'minute') {
                return options.filter((option) => option % 5 === 0)
            }
            return options
        },
        onTimeLinkClick() {
            if (!this.disabled) {
                this.showTime = true
            }
        },
        onTimeConfirm(data) {
            console.log('onTimeConfirm', data)
            this.selectedTimeValue = data
            this.showTime = false
            this.changeData()
        },
        onTimeCancel() {
            this.showTime = false
        },
        onCalendarClearBtnClick() {
            console.log('onCalendarClearBtnClick')
            this.selectedCalendarValue = null
            this.changeData()
        },
        onTimeClearBtnClick() {
            console.log('onTimeClearBtnClick')
            this.selectedTimeValue = null
            this.changeData()
        },
        getCombinedDate() {
            if (this.selectedCalendarValue) {
                const format = this.dateFormat || 'yyyy-MM-dd'
                if (this.selectedTimeValue) {
                    const timeArr = this.selectedTimeValue.split(':')
                    console.log('timeArr', timeArr)
                    const offsetMs = (Number(timeArr[0]) * 60 + Number(timeArr[1])) * 60 * 1000
                    console.log('offsetMs', offsetMs)
                    const selectedCalendarDate = new Date(DateTimeFunc.formatDate(this.selectedCalendarValue, format) + ' 00:00:00')
                    return new Date(selectedCalendarDate.getTime() + offsetMs)
                }
                else {
                    return new Date(DateTimeFunc.formatDate(this.selectedCalendarValue, format) + ' 00:00:00')
                }
            }
            else {
                return null
            }
        },
        changeData() {
            this.$emit('change-data', this.getCombinedDate())
        }
    },
}
</script>

<template>
    <!-- component -->
    <div class="calendar-selector van-cell no-padding">
        <van-row class="wp100">
            <van-col span="19">
                <van-cell
                    class="calendar-section"
                    :class="{ disabled: disabled, placeholder: dateLabel == datePlaceholder }"
                    :title="label"
                    :value="dateLabel"
                    center
                    @click="onCalendarLinkClick"
                >
                    <template #right-icon>
                        <div v-if="!disabled" class="l-inner-margin minor-color">
                            <div v-if="clearable">
                                <van-icon 
                                    v-if="selectedCalendarValue" 
                                    name="clear"
                                    size="16"
                                    @click.stop="onCalendarClearBtnClick"
                                />
                                <van-icon v-else name="arrow"/>
                            </div>
                            <div v-else>
                                <van-icon name="arrow"/>
                            </div>
                        </div>
                    </template>
                </van-cell>
            </van-col>
            <van-col span="5" class="hp100 vertical-center">
                <van-cell 
                    class="time-section "
                    :class="{ disabled: disabled, placeholder: timeLabel == timePlaceholder }"
                    :value="timeLabel"
                    center
                    @click="onTimeLinkClick"
                >
                    <template #right-icon>
                        <div v-if="!disabled" class="l-inner-margin minor-color">
                            <div v-if="clearable">
                                <van-icon 
                                    v-if="selectedTimeValue" 
                                    name="clear"
                                    size="16"
                                    @click.stop="onTimeClearBtnClick"
                                />
                                <van-icon v-else name="arrow"/>
                            </div>
                            <div v-else>
                                <van-icon name="arrow"/>
                            </div>
                        </div>
                    </template>
                </van-cell>
            </van-col>
        </van-row>
        <van-calendar
            v-model="showCalendar"
            :show-confirm="false"
            :default-date="defaultDate"
            :min-date="minDate"
            color="#4AA2F7"
            @confirm="onCalendarConfirm"
        />
        <van-popup v-model="showTime" position="bottom">
            <van-datetime-picker
                type="time"
                :title="label"
                :filter="timeFilter"
                @confirm="onTimeConfirm"
                @cancel="onTimeCancel"
            />
        </van-popup>
    </div>
</template>

<style scoped>
.calendar-section {
    padding: 10px 0px 10px 16px;
}
.time-section {
    padding: 10px 16px 10px 0px;
    text-align: right;
}
.calendar-section .van-cell__title {
    flex: 7;
}
.calendar-section .van-cell__value {
    flex: 8;
}
</style>